import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Plus, MapPin, Phone, Star, PencilSimple, Trash, CheckCircle, Buildings, Power, MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import useGoogleMaps from "@/lib/useGoogleMaps";
import PhoneInput10 from "@/components/site/PhoneInput10";
import { toE164, to10Digit } from "@/lib/phone";

const EMPTY_FORM = {
    name: "", address_line: "", city: "", pincode: "", state: "", country: "India",
    phone: "", email: "", website: "", rooms: "", chairs: "",
};

export default function PracticeLocations() {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [busy, setBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = () => {
        api.get("/doctor/practice-locations", { params: { include_inactive: true } })
            .then((r) => setLocations(r.data || []))
            .catch(() => setLocations([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const openAdd = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setDrawerOpen(true);
    };
    const openEdit = (loc) => {
        setEditingId(loc.id);
        setForm({
            name: loc.name || "", address_line: loc.address_line || "",
            city: loc.city || "", pincode: loc.pincode || "", state: loc.state || "",
            country: loc.country || "India",
            phone: to10Digit(loc.phone || ""), email: loc.email || "", website: loc.website || "",
            rooms: loc.rooms ?? "", chairs: loc.chairs ?? "",
        });
        setDrawerOpen(true);
    };

    const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        const trimmed = {
            ...form,
            name: form.name.trim(),
            address_line: form.address_line.trim(),
            city: form.city.trim(),
            phone: form.phone ? (toE164(form.phone) || form.phone.trim()) : "",
            rooms: form.rooms === "" ? null : Number(form.rooms),
            chairs: form.chairs === "" ? null : Number(form.chairs),
        };
        if (!trimmed.name || trimmed.name.length < 2) { toast.error("Name is required"); return; }
        if (!trimmed.address_line || trimmed.address_line.length < 2) { toast.error("Address is required"); return; }
        if (!trimmed.city) { toast.error("City is required"); return; }
        setBusy(true);
        try {
            if (editingId) {
                await api.put(`/doctor/practice-locations/${editingId}`, trimmed);
                toast.success(`${trimmed.name} updated`);
            } else {
                await api.post("/doctor/practice-locations", trimmed);
                toast.success(`${trimmed.name} added`);
            }
            setDrawerOpen(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save location");
        } finally { setBusy(false); }
    };

    const setPrimary = async (loc) => {
        try {
            await api.put(`/doctor/practice-locations/${loc.id}/set-primary`);
            toast.success(`${loc.name} is now your primary location`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't update primary location");
        }
    };

    const toggleActive = async (loc) => {
        try {
            const { data } = await api.put(`/doctor/practice-locations/${loc.id}/toggle-active`);
            toast.success(data.is_active ? `${loc.name} reactivated` : `${loc.name} deactivated`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't update status");
        }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/doctor/practice-locations/${confirmDelete.id}`);
            toast.success(`${confirmDelete.name} removed`);
            setConfirmDelete(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't remove location");
        }
    };

    return (
        <div data-testid="doctor-practice-locations" className="space-y-4">
            {/* Header — compact action bar (no big page title since this
                component now lives inside the Doctor Profile Locations tab). */}
            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                    Manage every clinic you operate from. Primary location shows on your public profile and patient bookings default to it.
                </p>
                <Button
                    onClick={openAdd}
                    size="sm"
                    className="rounded-full shrink-0"
                    data-testid="add-location-btn"
                >
                    <Plus size={14} className="mr-1.5" weight="bold" /> Add location
                </Button>
            </div>

            {/* CONTENT — list view (2026-02-28: switched from card grid to
                row list to match Clinic Staff tab. Same scannable layout
                doctors already know from managing their team.) */}
            {loading ? (
                <div className="text-center text-muted-foreground text-sm py-16">Loading locations…</div>
            ) : locations.length === 0 ? (
                <EmptyState onAdd={openAdd} />
            ) : (
                <div className="bg-card border border-border rounded-2xl overflow-hidden">
                    {locations.map((loc) => (
                        <LocationRow
                            key={loc.id}
                            loc={loc}
                            onEdit={() => openEdit(loc)}
                            onSetPrimary={() => setPrimary(loc)}
                            onToggleActive={() => toggleActive(loc)}
                            onDelete={() => setConfirmDelete(loc)}
                        />
                    ))}
                </div>
            )}

            {/* ADD / EDIT DIALOG */}
            <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
                <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="location-drawer">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Edit practice location" : "Add practice location"}</DialogTitle>
                        <DialogDescription>
                            Use Google to find a clinic or enter clinic details manually.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        {/* Google Places autocomplete — fills name + address + city + state + pincode + country in one tap. */}
                        <PlacesAutocomplete onPick={(p) => setForm((f) => ({ ...f, ...p }))} />

                        <Field label="Clinic name *">
                            <Input
                                value={form.name}
                                onChange={(e) => onField("name", e.target.value)}
                                placeholder="e.g. SmileCraft — Indiranagar"
                                className="h-10"
                                data-testid="loc-form-name"
                            />
                        </Field>
                        <Field label="Clinic address *">
                            <Textarea
                                value={form.address_line}
                                onChange={(e) => onField("address_line", e.target.value)}
                                placeholder="Building / Floor, Street, Landmark"
                                rows={3}
                                className="resize-none"
                                data-testid="loc-form-address"
                            />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="City *">
                                <Input
                                    value={form.city}
                                    onChange={(e) => onField("city", e.target.value)}
                                    className="h-10"
                                    data-testid="loc-form-city"
                                />
                            </Field>
                            <Field label="State">
                                <Input
                                    value={form.state}
                                    onChange={(e) => onField("state", e.target.value)}
                                    className="h-10"
                                    data-testid="loc-form-state"
                                />
                            </Field>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <Field label="PIN code">
                                <Input
                                    value={form.pincode}
                                    onChange={(e) => onField("pincode", e.target.value)}
                                    className="h-10"
                                    data-testid="loc-form-pincode"
                                />
                            </Field>
                            <Field label="Country">
                                <Input
                                    value={form.country}
                                    onChange={(e) => onField("country", e.target.value)}
                                    className="h-10"
                                    data-testid="loc-form-country"
                                />
                            </Field>
                            <Field label="Clinic phone">
                                <PhoneInput10
                                    value={form.phone}
                                    onChange={(v) => onField("phone", v)}
                                    inputClassName="h-10"
                                    testid="loc-form-phone"
                                />
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Email">
                                <Input
                                    value={form.email}
                                    onChange={(e) => onField("email", e.target.value)}
                                    type="email"
                                    className="h-10"
                                    data-testid="loc-form-email"
                                />
                            </Field>
                            <Field label="Website URL">
                                <Input
                                    value={form.website}
                                    onChange={(e) => onField("website", e.target.value)}
                                    placeholder="https://…"
                                    type="url"
                                    className="h-10"
                                    data-testid="loc-form-website"
                                />
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/60">
                            <Field label="Rooms" hint="Optional">
                                <Input
                                    value={form.rooms}
                                    onChange={(e) => onField("rooms", e.target.value.replace(/[^0-9]/g, ""))}
                                    inputMode="numeric"
                                    className="h-10"
                                    data-testid="loc-form-rooms"
                                />
                            </Field>
                            <Field label="Chairs" hint="Optional">
                                <Input
                                    value={form.chairs}
                                    onChange={(e) => onField("chairs", e.target.value.replace(/[^0-9]/g, ""))}
                                    inputMode="numeric"
                                    className="h-10"
                                    data-testid="loc-form-chairs"
                                />
                            </Field>
                        </div>
                    </div>

                    <DialogFooter className="flex-row gap-2 justify-end mt-2">
                        <Button variant="outline" onClick={() => setDrawerOpen(false)} disabled={busy} data-testid="loc-form-cancel">
                            Cancel
                        </Button>
                        <Button onClick={save} disabled={busy} data-testid="loc-form-save">
                            {busy ? "Saving…" : editingId ? "Save changes" : "Add location"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* DELETE CONFIRM */}
            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent data-testid="loc-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove this location?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <span className="font-semibold text-foreground">{confirmDelete?.name}</span> will no longer appear on your profile.
                            Past appointments and cases tied to it stay intact.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="loc-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="loc-delete-confirm-btn">
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <Label className="text-xs font-semibold flex items-center gap-1.5">
                {label}
                {hint && <span className="text-[10px] font-normal text-muted-foreground italic">· {hint}</span>}
            </Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}

function LocationRow({ loc, onEdit, onSetPrimary, onToggleActive, onDelete }) {
    const isInactive = !loc.is_active;
    const cityLine = [loc.city, loc.state, loc.pincode].filter(Boolean).join(" · ");
    return (
        <div
            data-testid={`loc-card-${loc.id}`}
            className={`grid grid-cols-12 gap-3 px-5 py-4 border-b border-border last:border-b-0 items-center ${isInactive ? "opacity-60" : ""}`}
        >
            {/* LEFT — clinic name + status pills + capacity chips */}
            <div className="col-span-12 md:col-span-5 flex items-center gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
                    loc.is_primary && !isInactive ? "bg-primary text-white" : "bg-primary/10 text-primary"
                }`}>
                    <Buildings size={18} weight="duotone" />
                </div>
                <div className="min-w-0">
                    <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                        <span className="truncate">{loc.name}</span>
                        {loc.is_primary && !isInactive && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary uppercase font-semibold tracking-wider">
                                <Star size={9} weight="fill" /> Primary
                            </span>
                        )}
                        {isInactive && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 uppercase font-semibold">Inactive</span>
                        )}
                    </div>
                    {(loc.rooms || loc.chairs) && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                            {[
                                loc.rooms ? `${loc.rooms} ${loc.rooms === 1 ? "room" : "rooms"}` : null,
                                loc.chairs ? `${loc.chairs} ${loc.chairs === 1 ? "chair" : "chairs"}` : null,
                            ].filter(Boolean).join(" · ")}
                        </div>
                    )}
                </div>
            </div>

            {/* MIDDLE — address + city + phone */}
            <div className="col-span-12 md:col-span-4 min-w-0 text-sm space-y-0.5">
                <div className="flex items-start gap-1.5">
                    <MapPin size={12} className="mt-0.5 shrink-0 text-muted-foreground" />
                    <span className="truncate text-foreground/85" title={loc.address_line}>{loc.address_line}</span>
                </div>
                {cityLine && <div className="text-xs text-muted-foreground pl-[18px] truncate">{cityLine}</div>}
                {loc.phone && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        <Phone size={11} /> <span className="truncate">{loc.phone}</span>
                    </div>
                )}
            </div>

            {/* RIGHT — action buttons */}
            <div className="col-span-12 md:col-span-3 flex gap-2 md:justify-end flex-wrap">
                <Button variant="outline" size="sm" onClick={onEdit} data-testid={`loc-action-edit-${loc.id}`}>
                    <PencilSimple size={13} className="mr-1" /> Edit
                </Button>
                {!loc.is_primary && !isInactive && (
                    <Button variant="outline" size="sm" onClick={onSetPrimary} data-testid={`loc-action-primary-${loc.id}`}>
                        <Star size={13} weight="fill" className="mr-1" /> Set primary
                    </Button>
                )}
                {/* Activate / Deactivate — disabled for primary while active */}
                {!(loc.is_primary && !isInactive) && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onToggleActive}
                        className={isInactive ? "text-emerald-700 border-emerald-200 hover:bg-emerald-50" : "text-rose-600 border-rose-200 hover:bg-rose-50"}
                        data-testid={`loc-action-toggle-${loc.id}`}
                    >
                        <Power size={13} className="mr-1" /> {isInactive ? "Enable" : "Disable"}
                    </Button>
                )}
                {!loc.is_primary && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onDelete}
                        className="text-rose-700 hover:bg-rose-50"
                        data-testid={`loc-action-delete-${loc.id}`}
                        aria-label="Remove permanently"
                    >
                        <Trash size={13} />
                    </Button>
                )}
            </div>
        </div>
    );
}

function EmptyState({ onAdd }) {
    return (
        <div className="rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-secondary grid place-items-center mb-3">
                <Buildings size={20} className="text-muted-foreground" />
            </div>
            <div className="font-display text-lg font-bold">Add your first clinic</div>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
                Tell patients where to find you. The first location you add automatically becomes your primary.
            </p>
            <Button onClick={onAdd} className="rounded-full mt-5" data-testid="empty-state-add-btn">
                <Plus size={14} className="mr-1.5" weight="bold" /> Add location
            </Button>
            <div className="mt-3 text-[11px] text-muted-foreground inline-flex items-center gap-1">
                <CheckCircle size={11} weight="fill" className="text-primary" />
                Patient bookings default to your primary location
            </div>
        </div>
    );
}

/* Google Places autocomplete — tap a result and the form is pre-filled with
   the parsed address components. The component stays inert if the API key is
   missing so doctors on networks where Google is blocked still see the manual
   form (just without the autocomplete shortcut). */
function PlacesAutocomplete({ onPick }) {
    const { ready, error } = useGoogleMaps();
    const inputRef = React.useRef(null);
    const autocompleteRef = React.useRef(null);

    React.useEffect(() => {
        if (!ready || !inputRef.current || autocompleteRef.current) return;
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
            types: ["establishment"],
            fields: ["name", "address_components", "formatted_address", "geometry"],
        });
        autocompleteRef.current = ac;
        ac.addListener("place_changed", () => {
            const place = ac.getPlace();
            if (!place || !place.address_components) return;
            const get = (type, short = false) => {
                const c = place.address_components.find((c) => c.types.includes(type));
                return c ? (short ? c.short_name : c.long_name) : "";
            };
            const street = [get("street_number"), get("route"), get("sublocality_level_2"), get("sublocality_level_1"), get("sublocality")]
                .filter(Boolean).join(", ");
            // Some establishments don't have a street_number — fall back to formatted address minus city/state/pin.
            const fullAddress = street || place.formatted_address || "";
            onPick({
                name: place.name || "",
                address_line: fullAddress,
                city: get("locality") || get("administrative_area_level_2") || "",
                state: get("administrative_area_level_1") || "",
                pincode: get("postal_code") || "",
                country: get("country") || "India",
            });
        });
    }, [ready, onPick]);

    return (
        <div data-testid="loc-form-places-autocomplete">
            <Label className="text-xs font-semibold">Find your clinic on Google</Label>
            <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {/* tiny G mark replaced with a magnifier so we don't ship Google's logo without a license check */}
                    <MagnifyingGlass size={14} weight="bold" />
                </span>
                <Input
                    ref={inputRef}
                    placeholder={ready ? "Search for a place…" : (error ? "Manual entry only" : "Loading Google Maps…")}
                    className="h-10 pl-9"
                    disabled={!ready}
                    data-testid="loc-form-places-input"
                />
            </div>
            {error && (
                <p className="text-[10px] text-amber-700 mt-1">
                    Google Maps autocomplete isn't available right now — fill the fields manually below.
                </p>
            )}
        </div>
    );
}

